import { capitalCase } from 'change-case';
import find from 'lodash/find.js';

import Model from '@/models/Model.js';
import moment from '@/plugins/vue-moment.js';

export default class AutomatedSurvey extends Model {
    static entity = 'automated_surveys';

    static modelName = 'AutomatedSurvey';

    static fields() {
        return {
            id: this.attr(null),
            course_id: this.attr(null),
            survey_id: this.attr(null),
            evaluation_template_id: this.attr(null),
            parent_id: this.attr(null),
            group_id: this.attr(null),
            triggered_by: this.attr(null),
            triggerable_at: this.attr(null),
            triggerable_id: this.attr(null),
            triggerable_type: this.attr(null),
            triggerable_type_entity: this.attr(null),
            subject_id: this.attr(null),
            subject_type: this.attr(null),
            subject_type_entity: this.attr(null),
            enrollment_subject: this.attr(null),
            first_triggered_at: this.attr(null),
            last_triggered_at: this.attr(null),
            locked_after_seconds: this.attr(null),
            required_after_seconds: this.attr(null),
            is_anonymous: this.attr(null),
            time_limit: this.attr(null),
            is_editable: this.boolean(false),
            is_deletable: this.boolean(false),
            in_progress_count: this.attr(null),
            completed_count: this.attr(null),
            released_count: this.attr(null),
            not_started_count: this.attr(null),
            survey_attempts_count: this.attr(null),

            assignments_count: this.attr(null),

            ...super.fields(),

            course: this.belongsTo('courses', 'course_id'),
            survey: this.belongsTo('quizzes', 'survey_id'),
            parent: this.belongsTo('automated_surveys', 'parent_id'),
            triggerable: this.morphTo('triggerable_id', 'triggerable_type_entity'),
            subject: this.morphTo('subject_id', 'subject_type_entity'),
            assignments: this.hasMany('automated_survey_assignments', 'automated_survey_id'),
            targets: this.hasMany('automated_survey_targets', 'automated_survey_id'),
        };
    }

    get triggered_by_description() {
        return capitalCase(this.triggered_by);
    }

    get locked_after_display() {
        if (this.locked_after_seconds === null) {
            return '';
        }

        if (this.locked_after_seconds === 0) {
            return 'Immediately';
        }

        return this.fromSecondsToDisplay('locked_after_seconds');
    }

    get required_after_display() {
        if (this.required_after_seconds === null) {
            return '';
        }

        if (this.required_after_seconds === 0) {
            return 'Immediately';
        }

        return this.fromSecondsToDisplay('required_after_seconds');
    }

    get has_time_limit() {
        return !!this.time_limit;
    }

    get is_locked() {
        return !!this.locked_after_seconds;
    }

    get is_required() {
        return !!this.required_after_seconds;
    }

    get should_be_anonymous() {
        return this.is_anonymous || this.assignments.filter(assignment => assignment.is_anonymous).length > 0;
    }

    get should_have_triggered() {
        if (this.triggered_by !== 'date' || !this.triggerable_at) {
            return false;
        }

        return this.timestamp('triggerable_at').isBefore(moment()) && !this.first_triggered_at;
    }

    get time_limit_display() {
        return this.fromSecondsToDisplay('time_limit');
    }

    get subject_type_title() {
        if (!this.subject_type) {
            return '';
        }

        const model = this.subject_type.replace('App\\Models\\', '');

        return capitalCase(model);
    }

    get subject_link() {
        if (this.subject_type === 'App\\Models\\Activity') {
            return {
                name: 'activity-view',
                params: {
                    courseId: this.subject.course_id,
                    activityId: this.subject_id,
                },
            };
        }

        if (this.subject_type === 'App\\Models\\Course') {
            return {
                name: 'course-dashboard',
                params: {
                    courseId: this.subject.id,
                },
            };
        }

        if (this.subject_type === 'App\\Models\\CourseGrade') {
            return {
                name: 'course-dashboard',
                params: {
                    courseId: this.subject.course_id,
                },
            };
        }

        if (this.subject_type === 'App\\Models\\CourseEnrollment') {
            return {
                name: 'person-view',
                params: {
                    personId: this.subject.user_id,
                },
            };
        }

        return {};
    }

    get subject_title() {
        if (this.subject_type === 'App\\Models\\Activity' || this.subject_type === 'App\\Models\\Course') {
            return this.subject.title;
        }

        return this.subject.user.name;
    }

    get estimated_count() {
        return this.targets.reduce((sum, target) => sum + target.estimated_count, 0);
    }

    get enrollment_subject_description() {
        return {
            all: 'All Instructors',
            current_instructors: 'Current Instructors Only',
            past_instructors: 'Past Instructors Only',
        }[this.enrollment_subject];
    }

    get deployment_details_link() {
        return {
            name: 'survey-deployment-details',
            params: {
                quizId: this.survey_id,
                automatedSurveyId: this.id,
            },
        };
    }

    surveyVersion(versions) {
        return find(versions, { versionable_id: this.survey_id, versionable_type: 'App\\Models\\Survey' });
    }

    isUsingLatestVersion(versions) {
        const versionUsed = this.surveyVersion(versions);

        if (!versionUsed) {
            throw new Error('Could not identify which survey version is being used.');
        }

        return versionUsed.is_latest;
    }

    toForm() {
        return {
            survey_id: this.survey_id,
            triggered_by: this.triggered_by,
            triggerable_at: this.triggerable_at ? this.timestamp('triggerable_at').toDate() : null,
            triggerable_id: this.triggerable_id,
            subject_type: this.subject_type,
            subject_id: this.subject_id,
            enrollment_subject: this.enrollment_subject,
            locked_after_seconds: this.locked_after_seconds,
            required_after_seconds: this.required_after_seconds,
            is_anonymous: this.is_anonymous,
            time_limit: this.time_limit,
        };
    }
}
